import { DEMO_ENTITY } from '../catalogs/banks.js';
import { z } from 'zod';
import { MoneySchema } from '../common/money.js';
import { RateSchema } from '../common/rate.js';
/**
 * Modo de fondeo (`03-orquestador-pagos.md` §3). Se soportan **ambos** con la misma saga;
 * **por defecto `SETTLEMENT_ACCOUNT`** (cuenta puente de la entidad).
 * - `SETTLEMENT_ACCOUNT`: `CREDIT_AUTHORIZED → SENT_TO_BREB`; sin pasos sobre la cuenta del cliente.
 * - `LINKED_ACCOUNT_JIT`: `CREDIT_AUTHORIZED → FUNDING → FUNDED → SENT_TO_BREB` (crédito + inmovilización
 *   + débito en la cuenta vinculada; reverso si falla).
 */
export const FUNDING_MODES = ['SETTLEMENT_ACCOUNT', 'LINKED_ACCOUNT_JIT'];
export const FundingModeSchema = z.enum(FUNDING_MODES).meta({ id: 'FundingMode' });
export const DEFAULT_FUNDING_MODE = 'SETTLEMENT_ACCOUNT';
/** GMF (4×1000) parametrizable por modo de fondeo; desactivado en el demo. */
export const GmfConfigSchema = z.object({
    enabled: z.boolean(),
    rate: RateSchema,
});
/** Configuración de fondeo (`/admin/settings/funding`, `tenant.settings.funding`). */
export const FundingConfigSchema = z
    .object({
    mode: FundingModeSchema.default(DEFAULT_FUNDING_MODE),
    /** Selector por producto (`productCode` → modo); si falta, aplica `mode`. */
    productOverrides: z.record(z.string(), FundingModeSchema).optional(),
    /** Cuenta puente de la entidad (modo SETTLEMENT_ACCOUNT). */
    settlementAccount: z
        .object({
        participantCode: z.string(),
        accountNumber: z.string(),
        accountType: z.enum(['SAVINGS', 'CHECKING']),
        holderName: z.string(),
    })
        .optional(),
    gmf: z.object({
        SETTLEMENT_ACCOUNT: GmfConfigSchema,
        LINKED_ACCOUNT_JIT: GmfConfigSchema,
    }),
    /** SLA de Bre-B antes de pasar a PENDING_CONFIRMATION. */
    brebConfirmationTimeoutSeconds: z.int().min(5).max(120).default(20),
    /** Vencimiento de la autorización de crédito (hold). */
    authorizationTtlMinutes: z
        .int()
        .min(1)
        .max(24 * 60)
        .default(15),
})
    .meta({ id: 'FundingConfig' });
export const DEMO_FUNDING_CONFIG = {
    mode: 'SETTLEMENT_ACCOUNT',
    settlementAccount: {
        participantCode: '990',
        // Alineada con la semilla del sandbox (`services/sandbox/src/seed/data.ts`).
        accountNumber: '67000000001',
        accountType: 'CHECKING',
        holderName: `${DEMO_ENTITY.legalName} – Cuenta puente Paguz`,
    },
    gmf: {
        SETTLEMENT_ACCOUNT: { enabled: false, rate: '0.004000' },
        LINKED_ACCOUNT_JIT: { enabled: false, rate: '0.004000' },
    },
    brebConfirmationTimeoutSeconds: 20,
    authorizationTtlMinutes: 15,
};
/** Límites y riesgo transaccional (M8). Resultado: `ALLOW`, `CHALLENGE` (SCA adicional) o `DENY{reasonCode}`. */
export const LimitScopeSchema = z.enum(['TENANT', 'PRODUCT', 'CUSTOMER']);
export const LimitRuleTypeSchema = z
    .enum([
    'MIN_PER_TX',
    'MAX_PER_TX',
    'MAX_DAILY_AMOUNT',
    'MAX_MONTHLY_AMOUNT',
    'MAX_DAILY_COUNT',
    'VELOCITY',
    'NEW_PAYEE_THRESHOLD',
    'BLOCKED_MCC',
    'BLOCKED_ALIAS',
])
    .meta({ id: 'LimitRuleType' });
export const LimitActionSchema = z.enum(['DENY', 'CHALLENGE']);
export const LimitResultSchema = z.enum(['ALLOW', 'CHALLENGE', 'DENY']).meta({ id: 'LimitResult' });
export const LimitRuleParamsSchema = z.discriminatedUnion('ruleType', [
    z.object({ ruleType: z.literal('MIN_PER_TX'), amount: MoneySchema }),
    z.object({ ruleType: z.literal('MAX_PER_TX'), amount: MoneySchema }),
    z.object({ ruleType: z.literal('MAX_DAILY_AMOUNT'), amount: MoneySchema }),
    z.object({ ruleType: z.literal('MAX_MONTHLY_AMOUNT'), amount: MoneySchema }),
    z.object({ ruleType: z.literal('MAX_DAILY_COUNT'), count: z.int().min(1) }),
    z.object({
        ruleType: z.literal('VELOCITY'),
        count: z.int().min(1),
        windowMinutes: z.int().min(1),
    }),
    z.object({ ruleType: z.literal('NEW_PAYEE_THRESHOLD'), amount: MoneySchema }),
    z.object({ ruleType: z.literal('BLOCKED_MCC'), mccs: z.array(z.string().regex(/^\d{4}$/)) }),
    z.object({
        ruleType: z.literal('BLOCKED_ALIAS'),
        aliases: z.array(z.object({ aliasType: z.string(), aliasValue: z.string() })),
    }),
]);
export const LimitRuleSchema = z
    .object({
    id: z.string(),
    scope: LimitScopeSchema,
    scopeRef: z.string().nullable(),
    params: LimitRuleParamsSchema,
    action: LimitActionSchema,
    active: z.boolean(),
})
    .meta({ id: 'LimitRule' });
/** Límites del demo: compra mínima $10.000, máx. $3.000.000 por compra y $5.000.000 diario. */
export const DEMO_LIMIT_RULES = [
    {
        scope: 'TENANT',
        scopeRef: null,
        action: 'DENY',
        active: true,
        params: { ruleType: 'MIN_PER_TX', amount: { amount: '10000.00', currency: 'COP' } },
    },
    {
        scope: 'TENANT',
        scopeRef: null,
        action: 'DENY',
        active: true,
        params: { ruleType: 'MAX_PER_TX', amount: { amount: '3000000.00', currency: 'COP' } },
    },
    {
        scope: 'TENANT',
        scopeRef: null,
        action: 'DENY',
        active: true,
        params: { ruleType: 'MAX_DAILY_AMOUNT', amount: { amount: '5000000.00', currency: 'COP' } },
    },
];
//# sourceMappingURL=config.js.map